import type { Item } from '../shared/model.ts';
import type { ViewState } from '../state/viewstate.ts';
// What the kreska says about the resident's own lines (Decision 38, #89).
//
// It lives in `derive/` rather than beside the kreska because of what it now
// counts: the vehicles INSIDE the viewfinder, handed to it as
// `derive()`'s `summary.vehicles` (SPEC-viewfinder §2.5). The copy and the
// Polish counts are the same decision as `voices.ts`'s run labels and source
// notes — a sentence derived from a snapshot, with no DOM and no clock.
import type { Freshness } from './freshness.ts';

/**
 * The three pieces of „Linie 16, 174, 2 pojazdy, **1 opóźniony**": the prefix,
 * the part in brick — a delay is the one thing on this line that is wrong — and
 * the tail. Three fields rather than one string because only the middle is
 * coloured, and a colour is never spliced into copy.
 */
export type Summary = { a: string; b: string; c: string };

/** The same threshold `delayLabel` uses: one minute late is not a callout (§4.4). */
const DELAYED_MIN = 2;

/** `pojazd / pojazdy / pojazdów`, as the prototype implements it (§7.2). */
function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const last = n % 10;
  const tens = n % 100;
  return last >= 2 && last <= 4 && (tens < 10 || tens >= 20) ? few : many;
}

const named = (lines: string[]) => `${lines.length === 1 ? 'Linia' : 'Linie'} ${lines.join(', ')}`;

export type SummaryInput = {
  view: Pick<ViewState, 'lines' | 'onlyLine' | 'at'>;
  /**
   * The vehicles **in reach**: `derive()`'s `summary.vehicles`, which is the
   * set „Warstwy" counts narrowed to vehicles (SPEC-viewfinder §2.5). It was
   * everything the app held until V3 moved the count inside the viewfinder.
   *
   * The lens therefore looks at the ring and not at the city, which is what
   * §2.5 rules: „Linia 99: brak pojazdów na trasie" now means nothing of that
   * line is running IN REACH, and reads true either way.
   */
  items: Item[];
  /** The ZTM reading, because what we can honestly say depends on it. */
  ztm: Freshness['kind'];
};

/**
 * What the kreska says about the resident's own lines, at every grip position
 * including the 72 px dial. The ladder is the prototype's, in its order, with
 * one deviation: a ZTM that has given up says so instead of borrowing the
 * starting wording (§7.1, D-2).
 */
export function summary(input: SummaryInput): Summary {
  const { view, items, ztm } = input;
  const none = { a: '', b: '', c: '' };

  // With the centre moved in time there are no positions to summarise: ZTM
  // shows a note instead of vehicles, and the kreska says why (ruling 14).
  if (view.at != null) return { ...none, a: 'środek przeniesiony w czasie i miejscu' };

  const vehicles = items.filter((i) => i.kind === 'vehicle');
  const lens = view.onlyLine;

  // The line lens names its line even when nothing of it is running — that is
  // the whole point of naming it rather than showing an empty screen (§3.2).
  if (lens != null && !vehicles.some((v) => v.lines?.includes(lens))) {
    return { ...none, a: `Linia ${lens}: brak pojazdów na trasie` };
  }

  if (view.lines.length === 0) {
    return { ...none, a: 'Twoje linie: wybierz linie, którymi jeździsz' };
  }

  // Still connecting: we have their lines and nothing else to say about them.
  if (ztm === 'starting') return { ...none, a: `Twoje linie: ${view.lines.join(', ')}` };
  // Given up, with nothing ever loaded. The prototype renders this exactly like
  // the line above, for ever; §7.1 gives it its own sentence (D-2).
  if (ztm === 'dead') return { ...none, a: `${named(view.lines)}, nie znamy teraz pozycji` };

  const mine = vehicles.filter((v) => v.lines?.some((l) => view.lines.includes(l)));
  if (mine.length === 0) {
    const [only] = view.lines;
    return {
      ...none,
      a:
        view.lines.length === 1 && only
          ? `Linia ${only}: brak pojazdów na trasie`
          : 'Twoje linie: brak pojazdów na trasie',
    };
  }

  // One row and one count PER VEHICLE: a line has a dozen of them on the road
  // at rush hour, and „Linie 16, 174, 2 pojazdy" is a count of vehicles (§4.4).
  const a = `${named(view.lines)}, ${mine.length} ${plural(mine.length, 'pojazd', 'pojazdy', 'pojazdów')}, `;

  // A feed that stopped answering knows nothing about anyone's schedule, so the
  // honest tail is that we do not know, not that everything is fine.
  if (ztm === 'stale') return { a, b: '', c: 'opóźnienia nieznane' };

  const late = mine.filter((v) => v.delayMin != null && v.delayMin >= DELAYED_MIN).length;
  if (late > 0) {
    return { a, b: `${late} ${plural(late, 'opóźniony', 'opóźnione', 'opóźnionych')}`, c: '' };
  }
  return { a, b: '', c: 'wszystkie o czasie' };
}
